import { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";

// Vision UI Dashboard PRO React components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

// @mui material components
import Icon from "@mui/material/Icon";

// React Router
import { Link } from 'react-router-dom';

// React icons
import { IoChevronDown, IoChevronUp } from "react-icons/io5";

// Embla Carousel
import useEmblaCarousel from 'embla-carousel-react';

// API
import { takedownsApi } from "services/takedownsApi";

// Auth
import { useAuth } from "context/AuthContext";

// Images
import toddWakefieldSignature from "assets/images/todd_wakefield_signature.png";

// Helper function to extract root domain
const extractRootDomain = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch (e) {
    return url;
  }
};

// Helper function to calculate priority based on data
const calculatePriority = (data) => {
  if (data.violation_type === 'Explicit' && data.views > 100) return 'High';
  if (data.violation_type === 'Explicit') return 'Medium';
  return 'Low';
};

// Helper function to generate priority reason
const getPriorityReason = (data) => {
  if (data.violation_type === 'Explicit' && data.views > 100) {
    return "It's explicit content at a priority platform that may further harm the client as the content has higher views than average.";
  }
  if (data.violation_type === 'Explicit') {
    return "This is explicit content that requires immediate attention.";
  }
  return "Standard priority based on content type and platform.";
};

// Helper function to process HTML content and replace image paths
const processHtmlContent = (htmlContent) => {
  if (!htmlContent) return '';
  
  // Replace any references to the signature image with the imported path
  // This handles various possible path formats from the database
  let processedHtml = htmlContent;
  
  // Replace paths like: /src/assets/images/todd_wakefield_signature.png
  processedHtml = processedHtml.replace(
    /\/src\/assets\/images\/todd_wakefield_signature\.png(\?[^"'\s]*)?/gi,
    toddWakefieldSignature
  );
  
  // Replace paths like: src/assets/images/todd_wakefield_signature.png
  processedHtml = processedHtml.replace(
    /src\/assets\/images\/todd_wakefield_signature\.png(\?[^"'\s]*)?/gi,
    toddWakefieldSignature
  );
  
  // Replace paths like: assets/images/todd_wakefield_signature.png
  processedHtml = processedHtml.replace(
    /assets\/images\/todd_wakefield_signature\.png(\?[^"'\s]*)?/gi,
    toddWakefieldSignature
  );
  
  return processedHtml;
};

function TakedownDetails() {
  const { takedown_id } = useParams();
  const { currentUser } = useAuth();
  
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [takedownData, setTakedownData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [isCarouselHovered, setIsCarouselHovered] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);
  
  // Check if current user is admin
  const isAdmin = currentUser?.email === 'brian@massiveblue.ai';
  
  // Fetch takedown details on mount
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await takedownsApi.fetchTakedownDetails(takedown_id);
        setTakedownData(data);
        
        // Parse image URLs
        if (data.image_urls) {
          const imageArray = data.image_urls.split(',').map(url => url.trim()).filter(url => url);
          setImages(imageArray);
        } else {
          setImages([]);
        }
      } catch (err) {
        setError('Failed to load takedown details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (takedown_id) {
      fetchDetails();
    }
  }, [takedown_id]);
  
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  // Set up the onSelect callback
  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  const handleViewOriginalContent = () => {
    if (takedownData?.post_url) {
      window.open(takedownData.post_url, '_blank');
    }
  };

  const handleViewOriginalTOS = () => {
    if (takedownData?.tos_url) {
      window.open(takedownData.tos_url, '_blank');
    }
  };

  // Admin review action handlers
  const handleAccept = async () => {
    if (!takedownData || actionLoading) return;
    
    try {
      setActionLoading(true);
      await takedownsApi.acceptContent(takedownData.id);
      setActionSuccess('Content approved successfully!');
      
      // Refresh takedown data
      const updatedData = await takedownsApi.fetchTakedownDetails(takedown_id);
      setTakedownData(updatedData);
      
      setTimeout(() => setActionSuccess(null), 3000);
    } catch (err) {
      setError('Failed to approve content. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleHold = async () => {
    if (!takedownData || actionLoading) return;
    
    try {
      setActionLoading(true);
      await takedownsApi.holdContent(takedownData.id);
      setActionSuccess('Content put on hold successfully!');
      
      // Refresh takedown data
      const updatedData = await takedownsApi.fetchTakedownDetails(takedown_id);
      setTakedownData(updatedData);
      
      setTimeout(() => setActionSuccess(null), 3000);
    } catch (err) {
      setError('Failed to put content on hold. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!takedownData || actionLoading) return;
    
    try {
      setActionLoading(true);
      await takedownsApi.rejectContent(takedownData.id);
      setActionSuccess('Content rejected successfully!');
      
      // Refresh takedown data
      const updatedData = await takedownsApi.fetchTakedownDetails(takedown_id);
      setTakedownData(updatedData);
      
      setTimeout(() => setActionSuccess(null), 3000);
    } catch (err) {
      setError('Failed to reject content. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress color="info" size={60} />
        </VuiBox>
      </DashboardLayout>
    );
  }

  // Show error state
  if (error || !takedownData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3}>
          <Alert severity="error">{error || 'Takedown not found'}</Alert>
        </VuiBox>
      </DashboardLayout>
    );
  }

  // Calculate dynamic values
  const priority = calculatePriority(takedownData);
  const priorityReason = getPriorityReason(takedownData);
  const platformDomain = extractRootDomain(takedownData.post_url) || takedownData.host_name;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={2}>
          <VuiTypography variant="h5" color="white" fontWeight="bold">
            Takedown Details
          </VuiTypography>
        </VuiBox>

        <Grid container spacing={3}>
          {/* Main Content Card */}
          <Grid item xs={12}>
            <Card sx={{ 
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(60px)",
              borderRadius: "20px",
              
              p: 3
            }}>
              <VuiBox>
                {/* Threat Profile Header */}
                <VuiBox mb={3}>
                  <VuiTypography variant="h6" color="white" fontWeight="bold">
                    Takedown ID: {takedownData.id}
                  </VuiTypography>
                </VuiBox>

                <Grid container spacing={3}>
                  {/* Left Column - Content Information */}
                  <Grid item xs={12} lg={7} lineHeight="1.2">
                    <VuiBox>
                      {/* Takedown Info */}
                      <VuiBox mb={3}>
                        <VuiTypography 
                          variant="button" 
                          color="white" 
                          fontWeight="bold" 
                          mb={2}
                          sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                        >
                          TAKEDOWN INFO
                        </VuiTypography>
                        
                        <VuiBox display="flex" flexDirection="column" gap={1}>
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              STATUS
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                              {takedownData.takedown_status}
                            </VuiTypography>
                          </VuiBox>
                          
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              PRIORITY LEVEL
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                              {priority}
                            </VuiTypography>
                          </VuiBox>
                          
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              PRIORITY REASON
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px', maxWidth: '80%' }}>
                              {priorityReason}
                            </VuiTypography>
                          </VuiBox>
                        </VuiBox>
                      </VuiBox>

                      
                      {/* Divider */}
                  <Divider light sx={{ mx: 2, my: 1, mb: 2 }} />
                      

                      {/* Content Info and Stats Row */}
                      <Grid container spacing={3} mb={3} lineHeight="1.2">
                        <Grid item xs={12} md={6} lineHeight="1.2">
                          <VuiBox>
                            <VuiTypography 
                              variant="button" 
                              color="white" 
                              fontWeight="bold" 
                              mb={2}
                              sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                            >
                              CONTENT INFO
                            </VuiTypography>
                            
                            <VuiBox display="flex" flexDirection="column" gap={1}>
                              <VuiBox>
                                <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                  INFRINGEMENT TYPE
                                </VuiTypography>
                                <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                                  {takedownData.violation_type}
                                </VuiTypography>
                              </VuiBox>
                              
                              <VuiBox>
                                <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                  MEDIA TYPE
                                </VuiTypography>
                                <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                                  {takedownData.media_type}
                                </VuiTypography>
                              </VuiBox>
                            </VuiBox>
                          </VuiBox>
                        </Grid>

                        <Grid item xs={12} md={6} lineHeight="1.2">
                          <VuiBox>
                            <VuiTypography 
                              variant="button" 
                              color="white" 
                              fontWeight="bold" 
                              mb={2}
                              sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                            >
                              CONTENT STATS
                            </VuiTypography>
                            
                            <VuiBox display="flex" flexDirection="column" gap={1}>
                              <VuiBox>
                                <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                  VIEWS
                                </VuiTypography>
                                <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                                  {takedownData.views || 'N/A'}
                                </VuiTypography>
                              </VuiBox>
                              
                              <VuiBox>
                                <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                  LIKES
                                </VuiTypography>
                                <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                                  {takedownData.likes || 'N/A'}
                                </VuiTypography>
                              </VuiBox>
                              
                              <VuiBox>
                                <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                  COMMENTS
                                </VuiTypography>
                                <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                                  {takedownData.comments || 'N/A'}
                                </VuiTypography>
                              </VuiBox>
                            </VuiBox>
                          </VuiBox>
                        </Grid>
                      </Grid>

                      {/* Platform and Threat Actor Row */}
                      <Grid container spacing={3} lineHeight="1.2">
                        <Grid item xs={12} md={6}>
                          <VuiBox>
                            <VuiTypography 
                              variant="button" 
                              color="white" 
                              fontWeight="bold" 
                              mb={2}
                              sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                            >
                              PLATFORM
                            </VuiTypography>
                            
                            <VuiBox display="flex" flexDirection="column" gap={1}>
                              <VuiBox>
                                <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                  NAME
                                </VuiTypography>
                                <Link to={`/Dashboards/Platforms/${platformDomain}/details`} style={{ textDecoration: 'none' }}>
                                <VuiTypography 
                                  variant="button" 
                                    color="info" 
                                    fontWeight="medium" 
                                  sx={{ display: 'block', mt: 0.5, fontSize: '14px', textDecoration: "underline", cursor: "pointer" }}
                                >
                                    {platformDomain}
                                </VuiTypography>
                                </Link>
                              </VuiBox>
                              
                              <VuiBox>
                                <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                  CHANNEL TYPE
                                </VuiTypography>
                                <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                                  {takedownData.channel_type}
                                </VuiTypography>
                              </VuiBox>
                              
                              <VuiBox>
                                <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                  HOST COUNTRY
                                </VuiTypography>
                                <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                                  {takedownData.host_country}
                                </VuiTypography>
                              </VuiBox>
                            </VuiBox>
                          </VuiBox>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <VuiBox>
                            <VuiTypography 
                              variant="button" 
                              color="white" 
                              fontWeight="bold" 
                              mb={2}
                              sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                            >
                              THREAT ACTOR
                            </VuiTypography>
                            
                            <VuiBox display="flex" flexDirection="column" gap={1}>
                              <VuiBox>
                                <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                  USERNAME
                                </VuiTypography>
                                {takedownData.threatId ? (
                                  <Link to={`/dashboards/threats/${takedownData.threatId}/details`} style={{ textDecoration: 'none' }}>
                                  <VuiTypography 
                                    variant="button" 
                                      color="info" 
                                      fontWeight="medium" 
                                    sx={{ display: 'block', mt: 0.5, fontSize: '14px', textDecoration: "underline", cursor: "pointer" }}
                                  >
                                      {takedownData.username}
                                    </VuiTypography>
                                  </Link>
                                ) : (
                                  <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, fontSize: '14px' }}>
                                    {takedownData.username || 'N/A'}
                                  </VuiTypography>
                                )}
                              </VuiBox>
                            </VuiBox>
                          </VuiBox>
                        </Grid>
                      </Grid>
                    </VuiBox>
                  </Grid>

                  {/* Right Column - Content Preview */}
                  <Grid item xs={12} lg={5}>
                    <VuiBox>
                      <VuiBox 
                        display="flex" 
                        justifyContent="space-between" 
                        alignItems="center" 
                        mb={2}
                      >
                        
                        
                      </VuiBox>
                      
                      {/* Embla Carousel Container */}
                      {images.length > 0 ? (
                      <VuiBox 
                        ref={emblaRef}
                        onMouseEnter={() => setIsCarouselHovered(true)}
                        onMouseLeave={() => setIsCarouselHovered(false)}
                        sx={{
                          overflow: 'hidden',
                          borderRadius: '25px',
                          height: '400px',
                          position: 'relative',
                          backgroundColor: takedownData?.takedown_status === 'Removed' ? 'rgba(0, 0, 0, 0.95)' : 'transparent'
                        }}
                      >
                        <VuiBox 
                          sx={{
                            display: 'flex',
                            height: '100%'
                          }}
                        >
                          {images.map((image, index) => (
                            <VuiBox
                              key={index}
                              sx={{
                                flex: '0 0 100%',
                                minWidth: 0,
                                height: '400px',
                                backgroundImage: `url(${image})`,
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat'
                              }}
                            />
                          ))}
                        </VuiBox>

                        {/* Navigation Arrows */}
                        <VuiBox 
                          position="absolute" 
                          right="20px" 
                          top="50%" 
                          transform="translateY(-50%)"
                          zIndex={2}
                        >
                          <IconButton
                            onClick={scrollNext}
                            sx={{
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                              width: '40px',
                              height: '40px',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              },
                            }}
                          >
                            <IoChevronDown size="20px" style={{ transform: 'rotate(270deg)' }} />
                          </IconButton>
                        </VuiBox>

                        <VuiBox 
                          position="absolute" 
                          left="20px" 
                          top="50%" 
                          transform="translateY(-50%)"
                          zIndex={2}
                        >
                          <IconButton
                            onClick={scrollPrev}
                            sx={{
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                              width: '40px',
                              height: '40px',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              },
                            }}
                          >
                            <IoChevronUp size="20px" style={{ transform: 'rotate(270deg)' }} />
                          </IconButton>
                        </VuiBox>

                        {/* Content Removed Overlay */}
                        {takedownData?.takedown_status === 'Removed' && (
                          <VuiBox
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: 'rgba(0, 0, 0, 0.95)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 4,
                              borderRadius: '25px',
                            }}
                          >
                            <Icon
                              sx={{
                                fontSize: '80px',
                                color: '#01b574',
                                mb: 2
                              }}
                            >
                              check_circle
                            </Icon>
                            <VuiTypography
                              variant="h3"
                              color="white"
                              fontWeight="bold"
                              mb={1}
                              textAlign="center"
                            >
                              Content Has Been Removed
                            </VuiTypography>
                            <VuiTypography
                              variant="body2"
                              color="text"
                              textAlign="center"
                              sx={{ maxWidth: '400px' }}
                            >
                              This infringing content has been successfully removed from the platform
                            </VuiTypography>
                          </VuiBox>
                        )}

                        {/* Explicit Content Overlay */}
                        {takedownData?.violation_type === 'Explicit' && !isCarouselHovered && takedownData?.takedown_status !== 'Removed' && (
                          <VuiBox
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: 'rgba(0, 0, 0, 0.95)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 3,
                              borderRadius: '25px',
                              transition: 'opacity 0.3s ease-in-out',
                            }}
                          >
                            <Icon
                              sx={{
                                fontSize: '64px',
                                color: '#ff6b6b',
                                mb: 2
                              }}
                            >
                              warning
                            </Icon>
                            <VuiTypography
                              variant="h4"
                              color="white"
                              fontWeight="bold"
                              mb={1}
                              textAlign="center"
                            >
                              Explicit Content
                            </VuiTypography>
                            <VuiTypography
                              variant="body2"
                              color="text"
                              textAlign="center"
                              sx={{ maxWidth: '300px' }}
                            >
                              Hover over the carousel to view content
                            </VuiTypography>
                          </VuiBox>
                        )}
                      </VuiBox>
                      ) : (
                        <VuiBox 
                          sx={{
                            borderRadius: '25px',
                            height: '400px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(255, 255, 255, 0.05)'
                          }}
                        >
                          <VuiTypography variant="button" color="text">
                            No images available
                          </VuiTypography>
                        </VuiBox>
                      )}

                      {/* Image Counter */}
                      {images.length > 0 && (
                      <VuiBox textAlign="center" mt={2}>
                        <VuiTypography variant="button" color="white" fontWeight="regular">
                          CONTENT PREVIEW {selectedIndex + 1} OF {images.length}
                        </VuiTypography>
                      </VuiBox>
                      )}

                      {/* Admin Review Buttons */}
                      {isAdmin && (
                        <VuiBox mt={3} mb={2}>
                          {actionSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                              {actionSuccess}
                            </Alert>
                          )}
                          <VuiBox 
                            display="flex" 
                            justifyContent="center" 
                            gap={2}
                            sx={{
                              '@media (max-width: 768px)': {
                                flexDirection: 'column'
                              }
                            }}
                          >
                            <VuiButton
                              variant="outlined"
                              color="error"
                              onClick={handleReject}
                              disabled={actionLoading}
                              sx={{
                                border: '2px solid #e51313',
                                color: '#e51313',
                                padding: '12px 24px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                borderRadius: '12px',
                                minWidth: '120px',
                                '&:hover': {
                                  backgroundColor: 'rgba(229, 19, 19, 0.1)',
                                  border: '2px solid #e51313',
                                },
                                '&:disabled': {
                                  opacity: 0.5,
                                  cursor: 'not-allowed',
                                },
                              }}
                            >
                              {actionLoading ? <CircularProgress size={16} color="inherit" /> : 'Reject'}
                            </VuiButton>
                            
                            <VuiButton
                              variant="outlined"
                              color="white"
                              onClick={handleHold}
                              disabled={actionLoading}
                              sx={{
                                border: '2px solid white',
                                color: 'white',
                                padding: '12px 24px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                borderRadius: '12px',
                                minWidth: '120px',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  border: '2px solid white',
                                },
                                '&:disabled': {
                                  opacity: 0.5,
                                  cursor: 'not-allowed',
                                },
                              }}
                            >
                              {actionLoading ? <CircularProgress size={16} color="inherit" /> : 'Hold'}
                            </VuiButton>
                            
                            <VuiButton
                              variant="outlined"
                              color="success"
                              onClick={handleAccept}
                              disabled={actionLoading}
                              sx={{
                                border: '2px solid #01b574',
                                color: '#01b574',
                                padding: '12px 24px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                borderRadius: '12px',
                                minWidth: '120px',
                                '&:hover': {
                                  backgroundColor: 'rgba(1, 181, 116, 0.1)',
                                  border: '2px solid #01b574',
                                },
                                '&:disabled': {
                                  opacity: 0.5,
                                  cursor: 'not-allowed',
                                },
                              }}
                            >
                              {actionLoading ? <CircularProgress size={16} color="inherit" /> : 'Accept'}
                            </VuiButton>
                          </VuiBox>
                        </VuiBox>
                      )}
                    </VuiBox>
                    {/* Only show VIEW ORIGINAL CONTENT button if status is not Removed */}
                    {takedownData?.takedown_status !== 'Removed' && (
                    <VuiBox display="flex" justifyContent="flex-end" mt={2}>
                      <VuiButton 
                        variant="contained"
                        color="info"
                        size="small"
                        onClick={handleViewOriginalContent}
                        sx={{
                          fontSize: ".65rem",
                          fontWeight: "bold",
                          px: 2,
                          py: 1,
                          minHeight: "24px"
                        }}
                      >
                        VIEW ORIGINAL CONTENT
                      </VuiButton>
                    </VuiBox>
                    )}
                  </Grid>
                </Grid>
              </VuiBox>
            </Card>
          </Grid>

          {/* Takedown Terms Requirements Card */}
          <Grid item xs={12}>
            <Card sx={{ 
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(60px)",
              borderRadius: "20px",
              p: 3
            }}>
              <VuiBox>
                <VuiBox 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="center" 
                  mb={3}
                >
                  <VuiTypography 
                    variant="button" 
                    color="white" 
                    fontWeight="bold"
                    sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                  >
                    TAKEDOWN TERMS REQUIREMENTS
                  </VuiTypography>
                  <VuiButton
                    variant="contained"
                    color="info"
                    size="small"
                    onClick={handleViewOriginalTOS}
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: "bold",
                      px: 2,
                      py: 1,
                      minHeight: "24px"
                    }}
                  >
                    VIEW ORIGINAL TOS
                  </VuiButton>
                </VuiBox>
                
                <VuiTypography 
                  variant="body2" 
                  color="white" 
                  sx={{ 
                    whiteSpace: "pre-line",
                    fontSize: "14px"
                  }}
                >
                  {takedownData.notice_requirements || 'No takedown terms available'}
                </VuiTypography>
              </VuiBox>
            </Card>
          </Grid>

          {/* Takedown Notice Card */}
          <Grid item xs={12}>
            <Card sx={{ 
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(60px)",
              borderRadius: "20px",
              p: 3
            }}>
              <VuiBox>
                <VuiTypography 
                  variant="button" 
                  color="white" 
                  fontWeight="bold" 
                  mb={3}
                  sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                >
                  TAKEDOWN NOTICE
                </VuiTypography>
                
                <Card sx={{ 
                  background: "rgba(255, 255, 255, 0.75)",
                  borderRadius: "20px",
                  p: 3
                }}>
                  {takedownData.takedown_notice_content ? (
                  <VuiBox
                    sx={{ 
                      color: "black",
                      fontSize: "14px",
                      lineHeight: 1.6,
                      '& p': {
                        margin: '0 0 16px 0',
                        fontSize: "14px"
                      },
                      '& h3': {
                        color: "black",
                        fontSize: "16px",
                        fontWeight: "bold",
                        margin: '24px 0 16px 0'
                      },
                      '& strong': {
                        fontWeight: "bold"
                      },
                      '& ul': {
                        margin: '0 0 16px 0',
                        paddingLeft: '20px'
                      },
                      '& li': {
                        margin: '0 0 8px 0',
                        fontSize: "14px"
                      },
                      '& img': {
                        maxWidth: '150px',
                        height: 'auto',
                        margin: '16px 0'
                      }
                    }}
                      dangerouslySetInnerHTML={{ __html: processHtmlContent(takedownData.takedown_notice_content) }}
                    />
                  ) : (
                    <VuiBox sx={{ color: "black", fontSize: "14px", p: 2 }}>
                      <VuiTypography variant="body2" sx={{ color: "black" }}>
                        No takedown notice content available
                      </VuiTypography>
                    </VuiBox>
                  )}
                </Card>
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>
    </DashboardLayout>
  );
}

export default TakedownDetails;
